import { CredentialEntity, Logger, ServiceAuthType, ServiceAuthTypeMap } from '@uploadcam/common';
import { ExchangeAuthCodeInteractor } from './exchange-auth-code.interactor';
import { OAuthRequiredScopesMissingError } from '../errors/oauth-required-scopes-missing.error';
import { OAuthResponse } from '../models/oauth-response.model';
import { OAuthMissingResponseDataError } from '../errors/oauth-missing-response-data.error';
import { OAuthStateMismatchError } from '../errors/oauth-state-mismatch.error';
import { OAuthUserCanceledError } from '../errors/oauth-user-canceled.error';
import { AnalyticsService } from '../../../../app/features/shared/services/analytics.service';
import { AuthenticateWithCredentialsInteractor } from './authenticate-with-credentials.interactor';

export const LAST_AUTH_TYPE_KEY = 'uploadcam_last_auth_type';

export class LoginFinishInteractor {
  constructor(
    private readonly analytics: AnalyticsService,
    private readonly authenticateWithCredentials: AuthenticateWithCredentialsInteractor,
    private readonly exchangeAuthCode: ServiceAuthTypeMap<ExchangeAuthCodeInteractor>,
    private readonly logger: Logger,
    private readonly requiredScopes: ServiceAuthTypeMap<readonly string[]>,
    private readonly storage: Storage,
  ) {}

  public async execute(
    authType: ServiceAuthType,
    res: OAuthResponse,
    pkceVerifier: string,
    state: string,
    isPopup: boolean,
  ): Promise<void> {
    this.logger.log(`Finish login with: ${authType}`);

    const exchangeAuthCode = this.exchangeAuthCode[authType];

    // User canceled the login flow
    if (res.error === 'access_denied') {
      throw new OAuthUserCanceledError();
    }

    // Validate response data
    if (!this.hasData(res)) {
      throw new OAuthMissingResponseDataError();
    }

    // Validate state
    if (state !== res.state) {
      throw new OAuthStateMismatchError();
    }

    // Exchange "auth code" for the "OAuth tokens" (access & refresh).
    // For implicit-flow providers (e.g. Zoho client-based app) `code` is empty; the access token
    // arrives in `metadata` and `exchangeAuthCode` constructs the credential locally.
    this.logger.log(`Exchanging auth code for credentials...`);

    const exchangedCreds = await exchangeAuthCode.execute(
      res.code ?? '',
      pkceVerifier,
      isPopup,
      res.metadata,
    );
    const creds: CredentialEntity = {
      ...exchangedCreds,
      // Override `authType` to ensure it matches the one used in the login
      // flow.For example, the Microsoft mapper guesses the `authType` based on
      // the scopes, but the user might have selected less scopes, so it's not
      // fiable. This is tech debt, we need to fix it in the future.
      //
      // TODO: `exchangeAuthCode` should not return/guess the `authType` (e.g. there should a simpler `CredentialEntity` type)
      authType,
      metadata: { ...res.metadata, ...exchangedCreds.metadata },
    };

    this.logger.log(`Credentials received.`);

    // Check if the scopes are the same as the required
    const requiredScopes = this.requiredScopes[authType];

    this.logger.info(`User accepted scopes: ${creds.scopes.join(' ') || '[NONE]'}`);

    if (!requiredScopes.every((scope) => creds.scopes.includes(scope))) {
      this.logger.warn(
        `Not all required scopes were accepted. Required: ${requiredScopes.join(' ')}`,
      );
      throw new OAuthRequiredScopesMissingError();
    }

    await this.authenticateWithCredentials.execute(creds);

    // Save the last auth type in storage
    this.storage.setItem(LAST_AUTH_TYPE_KEY, authType);

    this.analytics.track('login_completed', { authType });
    this.logger.log(`Logged in Firebase.`);
  }

  private hasData(res: OAuthResponse): res is OAuthResponse & { state: string } {
    // Code-flow providers return `code`; implicit-flow providers (Zoho client-based)
    // deliver the access token via `metadata.access_token`.
    const hasCode = !!res.code;
    const hasImplicitToken = typeof res.metadata?.['access_token'] === 'string';
    return (hasCode || hasImplicitToken) && !!res.state;
  }
}
